import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'

/**
 * A panel anchored to a trigger, portalled to <body> and fixed-positioned.
 *
 * Every surface these open from clips: the page header rounds its corners over
 * an accent hairline, the DataTable clips its own corners over a horizontal
 * scroll area, and a drawer scrolls its body. All of those are `overflow:
 * hidden` or scrollable, and an absolutely-positioned panel inside them loses
 * whatever hangs past the edge. Anchoring in script is the only fix a future
 * `overflow` somewhere up the tree cannot undo.
 *
 * Menu, Select and DatePicker all need exactly this, so it lives here once.
 *
 * @param {'start'|'end'} [align]  which inline edge of the trigger the panel hangs from
 * @param {boolean} [matchWidth]   panel takes the trigger's width — what a select
 *                                 wants, since its options belong to its field
 * @param {() => void} [onClose]
 */
export default function usePopover({ align = 'end', matchWidth = false, onClose } = {}) {
  const [open, setOpen] = useState(false)
  /** `null` until measured, so the panel never paints at 0,0 first. */
  const [pos, setPos] = useState(null)
  const rootRef = useRef(null)
  const panelRef = useRef(null)
  const id = useId()

  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const close = useCallback(() => {
    setOpen(false)
    onCloseRef.current?.()
  }, [])

  const place = useCallback(() => {
    const anchor = rootRef.current
    const panel = panelRef.current
    if (!anchor || !panel) return

    const a = anchor.getBoundingClientRect()
    const p = panel.getBoundingClientRect()
    const gap = 6
    const margin = 8

    // Flip above the trigger when the panel would run off the bottom.
    const below = a.bottom + gap + p.height <= window.innerHeight - margin
    const top = below ? a.bottom + gap : Math.max(margin, a.top - gap - p.height)

    // `align` is expressed in logical terms; resolve it against the document's
    // direction so one prop works in both RTL and LTR.
    const rtl = getComputedStyle(document.documentElement).direction === 'rtl'
    const width = matchWidth ? a.width : p.width
    const startEdge = rtl ? a.right - width : a.left
    const endEdge = rtl ? a.left : a.right - width
    const left = Math.min(
      Math.max(margin, align === 'start' ? startEdge : endEdge),
      window.innerWidth - width - margin,
    )

    setPos({
      top: Math.round(top),
      left: Math.round(left),
      width: matchWidth ? Math.round(a.width) : undefined,
      placement: below ? 'below' : 'above',
    })
  }, [align, matchWidth])

  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return undefined
    }
    place()
    const onMove = () => place()
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [open, place])

  useEffect(() => {
    if (!open) return undefined

    const onPointerDown = (event) => {
      // The panel is no longer a DOM child of the trigger, so both have to be
      // consulted before treating a click as "outside".
      if (rootRef.current?.contains(event.target)) return
      if (panelRef.current?.contains(event.target)) return
      close()
    }
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return
      close()
      // Put the caret back on the trigger, or the next Tab restarts from the top
      // of the document.
      rootRef.current?.querySelector('button, [tabindex]')?.focus()
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, close])

  return { open, setOpen, close, pos, rootRef, panelRef, id, place }
}

/** Inline style for a measured panel; hidden for the frame before measurement. */
export const panelStyle = (pos, width) => ({
  top: pos?.top ?? 0,
  left: pos?.left ?? 0,
  width: pos?.width ?? width,
  visibility: pos ? 'visible' : 'hidden',
})
